import { Autosuggest, Modal, Text } from '@acin/ui-core';
import React, { useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from '../../../hooks';
import { useReviewObligations } from '../../ReviewObligationsProvider';
import { useCreateInternalRegulationObligationProcessMutation } from '../graphql/mutations/createInternalRegulationObligationProcess.generated';
import { useDeleteInternalRegulationObligationProcessMutation } from '../graphql/mutations/deleteInternalRegulationObligationProcess.generated';
import { useInternalProcessNamesQuery } from '../graphql/queries/internalProcessNames.generated';
import { useObligationProcesses } from '../hooks/useObligationProcesses';
import { MappingItem } from './MappingItem';
import * as SC from './ProcessMappings.styled';

export interface ProcessMappingsProps {
  className?: string;
  selectedObligationId?: string;
}

export const ProcessMappings: React.FC<ProcessMappingsProps> = ({ className, selectedObligationId }) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { activeObligation } = useReviewObligations();
  const { processes, loading, pageInfo, handleFetchMore } = useObligationProcesses(activeObligation?.id);
  const [localProcesses, setLocalProcesses] = useState(processes);
  const [searchInput, setSearchInput] = useState('');
  const [processOptions, setProcessOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProcessForDelete, setSelectedProcessForDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteProcess, { loading: deleteLoading }] = useDeleteInternalRegulationObligationProcessMutation();
  const [createProcess] = useCreateInternalRegulationObligationProcessMutation();
  const { data: processNamesData } = useInternalProcessNamesQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (processNamesData?.internalProcessNames) {
      const options = processNamesData.internalProcessNames.map((name) => ({
        label: name,
        value: name,
      }));
      setProcessOptions(options);
    }
  }, [processNamesData?.internalProcessNames]);

  React.useEffect(() => {
    setLocalProcesses(processes);
  }, [activeObligation?.id, processes?.length]);

  const handleDeleteProcess = (processId: string) => {
    const process = localProcesses.find((p) => p.id === processId);
    if (process) {
      setSelectedProcessForDelete({ id: processId, name: process.processName ?? '' });
      setIsModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedProcessForDelete) return;
    const { errors } = await deleteProcess({
      variables: {
        input: { id: selectedProcessForDelete.id },
      },
      errorPolicy: 'all',
    });
    if (errors?.length) return;
    setLocalProcesses((prev) => prev.filter((process) => process.id !== selectedProcessForDelete.id));
    setIsModalOpen(false);
    setSelectedProcessForDelete(null);
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setSelectedProcessForDelete(null);
  };

  useIntersectionObserver(sentinelRef, handleFetchMore);

  return (
    <SC.ProcessMappings className={className}>
      <SC.List>
        {localProcesses.map((process) => (
          <MappingItem key={process.id} id={process.id} name={process.processName} onDelete={handleDeleteProcess} />
        ))}
        {loading && pageInfo?.hasNextPage && <SC.LoadingIndicator />}
        {pageInfo?.hasNextPage && <SC.Sentinel ref={sentinelRef} />}
      </SC.List>
      <SC.InputSection>
        <Autosuggest
          placeholder="Add an Index Process"
          onChange={(value) => setSearchInput(value)}
          onSelect={async (item) => {
            if (!selectedObligationId) return;
            const { data } = await createProcess({
              variables: {
                input: {
                  name: item.label,
                  obligationId: selectedObligationId,
                },
              },
              errorPolicy: 'all',
            });
            if (data?.createInternalRegulationObligationProcess) {
              setLocalProcesses((prev) => [
                ...prev,
                {
                  ...data.createInternalRegulationObligationProcess,
                  __typename: 'InternalRegulationObligationProcess' as const,
                },
              ]);
            }
            setSearchInput('');
          }}
          items={processOptions}
        />
      </SC.InputSection>
      <Modal
        show={isModalOpen}
        title="Delete this Process?"
        footerButtonPrimary={{ label: 'Confirm', onClick: handleConfirmDelete, disabled: deleteLoading }}
        footerButtonSecondary={{ label: 'Cancel', onClick: handleCancelDelete }}
      >
        <Text>{`Are you sure you want to delete the following process: ${selectedProcessForDelete?.name}?`}</Text>
      </Modal>
    </SC.ProcessMappings>
  );
};
